// Copyright (c) 2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../context/FirebaseConfig';
import { AuthContext } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/i18n';
import './AccessDenied.scss';

const ACCESS_DENIED_TYPES = {
  STAMP_DISABLED: {
    titleKey: 'access_denied_stamp_title',
    messageKey: 'access_denied_stamp_message',
  },
  NO_PRODUCTION_LINES: {
    titleKey: 'access_denied_production_title',
    messageKey: 'access_denied_production_message',
  },
};

export const AccessDenied = ({ type = 'STAMP_DISABLED' }) => {
  const navigate = useNavigate();
  const [emailRevealed, setEmailRevealed] = useState(false);
  const { currentUser } = useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);

  const config = ACCESS_DENIED_TYPES[type] || ACCESS_DENIED_TYPES.STAMP_DISABLED;

  const handleBackToLogin = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      navigate('/login');
    }
  };

  const handleContactAdmin = () => {
    if (!emailRevealed) {
      setEmailRevealed(true);
    }
  };

  return (
    <div className='accessDeniedContainer'>
      <div className='accessDeniedCard'>
        <h1 className='appTitle'>UpCircle Stamp</h1>

        <svg
          className='warningIcon'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z'
          />
        </svg>

        <h2 className='accessDeniedTitle'>{t(config.titleKey)}</h2>
        <p className='accessDeniedMessage'>{t(config.messageKey)}</p>

        <div className='buttonContainer'>
          <button className='backButton' onClick={handleBackToLogin}>
            <svg className='backIcon' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
            </svg>
            {t('access_denied_back_to_login')}
          </button>
          <button className={`primaryButton ${emailRevealed ? 'revealed' : ''}`} onClick={handleContactAdmin}>
            <div className={`buttonContent ${emailRevealed ? 'flipped' : ''}`}>
              <div className='front'>{t('access_denied_contact_admin')}</div>
              <div className='back'>{t('access_denied_support_email')}</div>
            </div>
          </button>
        </div>

        <div className='cardFooter'>{t('access_denied_copyright')}</div>
      </div>
    </div>
  );
};

export default AccessDenied;
